import React from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import strings from '../../components/Language';
import { images } from '../../config/image';
import { api } from '../../components/config/api';
import { Helmet } from 'react-helmet';
import {
  IconZap, IconShield, IconChart, IconLock, IconUserPlus, IconLink,
  IconQuant, IconChevronRight, IconChat
} from '../../components/Icons';
import './landing.scss';

const CONTACT_URL = 'https://t.me/ctsbot_info';

// Hero preview card. Figures are illustrative and labelled as such in the UI.
const PREVIEW_BOTS = [
  { name: 'AI', pair: 'BTC/USDT', pct: '+4.82%' },
  { name: 'CTS', pair: 'ETH/USDT', pct: '+2.37%' },
  { name: 'DCA', pair: 'SOL/USDT', pct: '+1.19%' }
];
const PREVIEW_SPARK = 'M0 62 L22 56 L44 60 L66 46 L88 50 L110 38 L132 42 L154 30 L176 34 L198 22 L220 26 L242 14 L264 18 L286 8 L300 10';

class Front extends React.Component {
  constructor(props) {
    super(props);

    this.defaultMarketPairs = [
      { pair: 'BTCUSDT', base: 'BTC', displayPair: 'BTC/USDT' },
      { pair: 'ETHUSDT', base: 'ETH', displayPair: 'ETH/USDT' },
      { pair: 'SOLUSDT', base: 'SOL', displayPair: 'SOL/USDT' },
      { pair: 'BNBUSDT', base: 'BNB', displayPair: 'BNB/USDT' },
      { pair: 'XRPUSDT', base: 'XRP', displayPair: 'XRP/USDT' }
    ].map((item) => ({ ...item, price: null, change: 0 }));

    this.state = {
      marketPairs: this.defaultMarketPairs,
      lang: strings.getLanguage(),
      scrolled: false
    };
  }

  componentDidMount() {
    this.getLiveMarketData();
    this.marketInterval = setInterval(this.getLiveMarketData, 10000);
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  componentWillUnmount() {
    if (this.marketInterval) {
      clearInterval(this.marketInterval);
    }
    window.removeEventListener('scroll', this.onScroll);
  }

  onScroll = () => {
    const scrolled = window.scrollY > 8;
    if (scrolled !== this.state.scrolled) {
      this.setState({ scrolled });
    }
  };

  setLanguage = (lang) => {
    localStorage.setItem('language', lang);
    strings.setLanguage(lang);
    this.setState({ lang });
  };

  getLiveMarketData = () => {
    axios({
      method: 'GET',
      url: api.marketData,
      headers: {
        'Accept-Language': strings.getLanguage(),
      }
    }).then((response) => {
      if (response && response.status === 200 && Array.isArray(response.data)) {
        const marketPairs = this.defaultMarketPairs.map((item) => {
          const liveItem = response.data.find((market) => market.symbol === item.pair);
          if (!liveItem) {
            return item;
          }
          return {
            ...item,
            price: Number.parseFloat(liveItem.lastPrice || 0),
            change: Number.parseFloat(liveItem.priceChangePercent || 0)
          };
        });

        this.setState({ marketPairs });
      }
    }).catch(() => {
    });
  };

  formatPrice(price) {
    if (!price) {
      return '—';
    }
    return price.toLocaleString('en-US', {
      minimumFractionDigits: price >= 100 ? 2 : 4,
      maximumFractionDigits: price >= 100 ? 2 : 4
    });
  }

  renderHeader() {
    const { lang, scrolled } = this.state;
    return (
      <header className={'lp-header' + (scrolled ? ' is-scrolled' : '')}>
        <div className="lp-container lp-header-inner">
          <Link to="/" className="lp-logo" aria-label="CTS Bot">
            <img src={images['logo']} alt="CTS Bot" />
          </Link>

          <nav className="lp-nav" aria-label="Primary">
            <a href="#features">{strings.lp_features}</a>
            <a href="#how">{strings.lp_how}</a>
            <a href="#strategies">{strings.robot_strategy}</a>
            <a href="#markets">{strings.lp_markets}</a>
          </nav>

          <div className="lp-header-actions">
            <div className="lp-lang" role="group" aria-label="Language">
              <button type="button" className={lang === 'en' ? 'active' : ''} onClick={() => this.setLanguage('en')}>EN</button>
              <button type="button" className={lang === 'zh' ? 'active' : ''} onClick={() => this.setLanguage('zh')}>中文</button>
            </div>
            <Link to="/login" className="lp-link-login">{strings.lp_login}</Link>
            <Link to="/register" className="lp-btn lp-btn-primary lp-btn-sm">{strings.lp_get_started}</Link>
          </div>
        </div>
      </header>
    );
  }

  renderPreview() {
    return (
      <div className="lp-preview" aria-hidden="true">
        <div className="lp-preview-top">
          <div>
            <span className="lp-preview-label">{strings.lp_portfolio}</span>
            <strong className="lp-preview-value">12,480.36 <small>USDT</small></strong>
          </div>
          <span className="lp-preview-tag">{strings.lp_preview}</span>
        </div>

        <svg className="lp-spark" viewBox="0 0 300 70" preserveAspectRatio="none">
          <defs>
            <linearGradient id="lpSparkFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="lpSparkLine" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#8b5cf6" />
              <stop offset="100%" stopColor="#2dd4bf" />
            </linearGradient>
          </defs>
          <path d={`${PREVIEW_SPARK} L300 70 L0 70 Z`} fill="url(#lpSparkFill)" />
          <path d={PREVIEW_SPARK} fill="none" stroke="url(#lpSparkLine)" strokeWidth="2.2" />
        </svg>

        <div className="lp-preview-list-head">
          <span>{strings.lp_active_bots}</span>
          <span className="lp-live"><i />Live</span>
        </div>
        <ul className="lp-preview-list">
          {PREVIEW_BOTS.map((bot) => (
            <li key={bot.name}>
              <span className="lp-bot-avatar">{bot.name}</span>
              <span className="lp-bot-meta">
                <b>{bot.name} Bot</b>
                <small>{bot.pair}</small>
              </span>
              <span className="lp-pct up">{bot.pct}</span>
            </li>
          ))}
        </ul>
        <p className="lp-preview-note">{strings.lp_preview_note}</p>
      </div>
    );
  }

  render() {
    const { marketPairs } = this.state;
    const year = new Date().getFullYear();
    const canonicalUrl = `${window.location.origin}/`;
    const seoTitle = 'CTS Bot | Smart Crypto Trading Platform';
    const seoDescription = 'Trade smarter with CTS Bot. Automated crypto strategies, risk-managed execution, and a professional platform for modern traders.';
    const seoImage = `${window.location.origin}/apple-touch-icon.png`;
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'CTS Bot',
      url: canonicalUrl,
      description: seoDescription
    };

    const trustPoints = [strings.our_advantages_1, strings.our_advantages_2, strings.our_advantages_3];

    const features = [
      { Icon: IconZap, title: strings.emotionless_trading, desc: strings.emotionless_trading_desc },
      { Icon: IconChart, title: strings.higher_trading_speed, desc: strings.higher_trading_speed_desc },
      { Icon: IconShield, title: strings.risk_diversification, desc: strings.risk_diversification_desc }
    ];

    const steps = [
      { Icon: IconUserPlus, title: strings.lp_step1_title, desc: strings.lp_step1_desc },
      { Icon: IconLink, title: strings.lp_step2_title, desc: strings.lp_step2_desc },
      { Icon: IconQuant, title: strings.lp_step3_title, desc: strings.lp_step3_desc }
    ];

    const strategies = [
      { tag: 'Automation', title: strings.DCA, desc: strings.robot_strategy_dca_desc },
      { tag: 'Trend logic', title: strings.robot_strategy_bollinger_middle, desc: strings.robot_strategy_bollinger_middle_desc },
      { tag: 'Re-entry', title: strings.robot_strategy_bollinger_bottom, desc: strings.robot_strategy_bollinger_bottom_desc }
    ];

    const security = [
      { Icon: IconLock, text: strings.our_advantages_1 },
      { Icon: IconChart, text: strings.our_advantages_2 },
      { Icon: IconZap, text: strings.our_advantages_3 }
    ];

    return (
      <div className="lp">
        <Helmet>
          <title>{seoTitle}</title>
          <meta name="description" content={seoDescription} />
          <meta name="keywords" content="crypto trading bot, automated trading, quantitative trading, CTS Bot, bitcoin bot, algorithmic trading" />
          <meta name="robots" content="index, follow" />
          <link rel="canonical" href={canonicalUrl} />

          <meta property="og:type" content="website" />
          <meta property="og:title" content={seoTitle} />
          <meta property="og:description" content={seoDescription} />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:image" content={seoImage} />
          <meta property="og:site_name" content="CTS Bot" />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={seoTitle} />
          <meta name="twitter:description" content={seoDescription} />
          <meta name="twitter:image" content={seoImage} />

          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        </Helmet>

        {this.renderHeader()}

        <main>
          {/* Hero */}
          <section className="lp-hero">
            <div className="lp-container lp-hero-grid">
              <div className="lp-hero-copy">
                <span className="lp-eyebrow"><i />{strings.lp_badge}</span>
                <h1>{strings.online_trading_never_been_this_easy}</h1>
                <p className="lp-lead">{strings.online_trading_never_been_this_easy_desc}</p>

                <div className="lp-actions">
                  <Link to="/register" className="lp-btn lp-btn-primary">
                    {strings.lp_create_account}
                    <IconChevronRight size={18} strokeWidth={2.2} />
                  </Link>
                  <Link to="/login" className="lp-btn lp-btn-ghost">{strings.member_login}</Link>
                </div>

                <ul className="lp-trust">
                  {trustPoints.map((text) => (
                    <li key={text}>
                      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      {text}
                    </li>
                  ))}
                </ul>
              </div>

              {this.renderPreview()}
            </div>
          </section>

          {/* Live markets */}
          <section className="lp-section lp-section-tight" id="markets">
            <div className="lp-container">
              <div className="lp-markets">
                <div className="lp-markets-head">
                  <div>
                    <h2>{strings.lp_markets}</h2>
                    <p>{strings.lp_markets_desc}</p>
                  </div>
                  <span className="lp-live"><i />Live</span>
                </div>
                <div className="lp-table" role="table" aria-label={strings.lp_markets}>
                  <div className="lp-row lp-row-head" role="row">
                    <span role="columnheader">{strings.lp_pair}</span>
                    <span role="columnheader">{strings.lp_last_price}</span>
                    <span role="columnheader">{strings.lp_24h}</span>
                  </div>
                  {marketPairs.map((item) => (
                    <div className="lp-row" role="row" key={item.pair}>
                      <span role="cell" className="lp-pair">
                        <i className={`lp-coin lp-coin-${item.base.toLowerCase()}`}>{item.base.charAt(0)}</i>
                        <b>{item.base}</b><small>/USDT</small>
                      </span>
                      <span role="cell" className="lp-num">{this.formatPrice(item.price)}</span>
                      <span role="cell">
                        <em className={'lp-pct ' + (item.change < 0 ? 'down' : 'up')}>
                          {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)}%
                        </em>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Why CTS Bot */}
          <section className="lp-section" id="features">
            <div className="lp-container">
              <div className="lp-section-head">
                <span className="lp-kicker">{strings.lp_features}</span>
                <h2>{strings.lp_why_title}</h2>
                <p>{strings.lp_why_desc}</p>
              </div>
              <div className="lp-grid-3">
                {features.map(({ Icon, title, desc }) => (
                  <article key={title} className="lp-card">
                    <span className="lp-icon"><Icon size={22} /></span>
                    <h3>{title}</h3>
                    <p>{desc}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* How it works */}
          <section className="lp-section" id="how">
            <div className="lp-container">
              <div className="lp-section-head">
                <span className="lp-kicker">{strings.lp_how}</span>
                <h2>{strings.buy_and_sell_cryptocurrency}</h2>
                <p>{strings.lp_how_desc}</p>
              </div>
              <ol className="lp-steps">
                {steps.map(({ Icon, title, desc }, i) => (
                  <li key={title} className="lp-step">
                    <div className="lp-step-top">
                      <span className="lp-icon"><Icon size={22} /></span>
                      <span className="lp-step-num">0{i + 1}</span>
                    </div>
                    <h3>{title}</h3>
                    <p>{desc}</p>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          {/* Strategies */}
          <section className="lp-section" id="strategies">
            <div className="lp-container">
              <div className="lp-section-head">
                <span className="lp-kicker">{strings.lp_strategies}</span>
                <h2>{strings.robot_strategy}</h2>
                <p>{strings.lp_strategy_desc}</p>
              </div>
              <div className="lp-grid-3">
                {strategies.map((item) => (
                  <article key={item.title} className="lp-card lp-card-strategy">
                    <span className="lp-tag">{item.tag}</span>
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Security / advantages */}
          <section className="lp-section">
            <div className="lp-container">
              <div className="lp-security">
                <div className="lp-security-copy">
                  <span className="lp-kicker">{strings.our_advantages}</span>
                  <h2>{strings.lp_security_title}</h2>
                </div>
                <ul className="lp-security-list">
                  {security.map(({ Icon, text }) => (
                    <li key={text}>
                      <span className="lp-icon"><Icon size={20} /></span>
                      <span>{text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="lp-section">
            <div className="lp-container">
              <div className="lp-cta">
                <div>
                  <h2>{strings.easy_way_to_trade_your_cryptocurrency}</h2>
                  <p>{strings.easy_way_to_trade_your_cryptocurrency_desc}</p>
                </div>
                <div className="lp-actions">
                  <Link to="/register" className="lp-btn lp-btn-light">
                    {strings.lp_create_account}
                    <IconChevronRight size={18} strokeWidth={2.2} />
                  </Link>
                  <Link to="/login" className="lp-btn lp-btn-ghost">{strings.member_login}</Link>
                </div>
              </div>
            </div>
          </section>
        </main>

        <footer className="lp-footer">
          <div className="lp-container">
            <div className="lp-footer-grid">
              <div className="lp-footer-brand">
                <img src={images['logo']} alt="CTS Bot" />
                <p>{strings.lp_footer_desc}</p>
              </div>
              <div>
                <h4>{strings.lp_product}</h4>
                <a href="#features">{strings.lp_features}</a>
                <a href="#strategies">{strings.robot_strategy}</a>
                <a href="#markets">{strings.lp_markets}</a>
              </div>
              <div>
                <h4>{strings.lp_account}</h4>
                <Link to="/register">{strings.register}</Link>
                <Link to="/login">{strings.lp_login}</Link>
                <Link to="/forgot-password">{strings.forgot_password}</Link>
              </div>
              <div>
                <h4>{strings.lp_support}</h4>
                <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer">
                  <IconChat size={16} /> {strings.contact_us}
                </a>
              </div>
            </div>
            <div className="lp-footer-bottom">
              <span>© {year} CTS Bot. {strings.lp_rights}</span>
              <span className="lp-risk">{strings.lp_risk}</span>
            </div>
          </div>
        </footer>
      </div>
    );
  }
}

export default Front;
